import { Button } from "@shared-ui/button";
import { Dialog } from "@/components/ui/dialog";
import type { SpecTemplateImpact } from "@/features/catalog/services/catalog-api.service";

/**
 * What saving this template would do to the products already filed here.
 *
 * THE REASON THIS SCREEN HAS A CONFIRMATION AT ALL. `upsertSpecTemplate`
 * deletes and re-creates every attribute row, and there is no rename: correcting
 * a typo in an attribute name is indistinguishable from deleting one attribute
 * and adding another. Products key their `specifications` by attribute NAME, so
 * the stored values do not follow. They are not deleted -- they stop being
 * addressed by anything, which is worse, because nothing surfaces them again.
 *
 * Marking an attribute required is the other blind edit, and it is retroactive:
 * every product that never answered it becomes unpublishable, and the ones
 * already live stay live until someone edits them and is refused by a rule that
 * did not exist when they were published.
 *
 * Neither is visible from a screen showing a template. This is the catalogue
 * underneath it, counted.
 */
interface TemplateImpactDialogProps {
	categoryName: string;
	impact: SpecTemplateImpact;
	saving: boolean;
	error: string | null;
	onCancel: () => void;
	onConfirm: () => void;
}

const TITLE_ID = "template-impact-dialog-title";

export function TemplateImpactDialog({
	categoryName,
	impact,
	saving,
	error,
	onCancel,
	onConfirm,
}: TemplateImpactDialogProps) {
	/*
	 * EVERY bucket, because this sentence claims safety.
	 *
	 * `narrowedEnums` was missing from an earlier version, so dropping a value
	 * from an enum's allowed list -- which refuses every product holding it on any
	 * later save that carries specifications -- printed "nothing already stored is
	 * affected". A harmless predicate that does not read a destructive bucket is
	 * worse than no predicate.
	 */
	const strandedByAxis = (impact.droppedAxes ?? []).filter(
		(axis) => axis.variants > 0,
	);

	const harmless =
		impact.orphaned.length === 0 &&
		impact.newlyRequired.length === 0 &&
		impact.retyped.length === 0 &&
		impact.narrowedEnums.length === 0 &&
		// The fifth bucket, and it belongs here for the reason the paragraph
		// above gives: a predicate that claims safety while not reading a
		// destructive bucket is worse than no predicate. Filtered on `variants > 0`
		// because dropping an axis NOTHING uses genuinely is harmless, and
		// warning about it would train an operator to click through the warnings
		// that matter.
		strandedByAxis.length === 0;

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			// Same rule as the brand merge: Cancel is `disabled={saving}`, so
			// Escape must be too, or the shell reintroduces the exit that
			// disabling removed.
			onClose={() => {
				if (!saving) onCancel();
			}}
			className="grid max-h-[85vh] w-full max-w-xl gap-4 overflow-y-auto rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Save the template for {categoryName}?
				</h2>
				<p className="text-sm text-muted-foreground">
					{impact.productCount === 1
						? "1 product is filed under this category."
						: `${impact.productCount} products are filed under this category.`}
				</p>
			</div>

			{harmless ? (
				/*
				 * Said explicitly rather than by showing an empty dialog. A
				 * confirmation that appears with nothing in it reads as a bug, and
				 * an operator who cannot tell "no impact" from "impact not
				 * calculated" learns to click through both.
				 */
				<p className="rounded-2xl border bg-slate-50 p-3 text-sm">
					Nothing already stored is affected: no attribute is being dropped or
					renamed, none is newly required, no type changes, no allowed value is
					being withdrawn, and no variant loses the axis it is filed under.
				</p>
			) : null}

			{strandedByAxis.length > 0 ? (
				<div className="grid gap-2 rounded-2xl border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900">
					<p className="font-medium">
						Variants that lose the axis they vary on
					</p>
					<p>
						Removing a browse axis leaves every variant's stored value exactly
						where it is. Nothing describes it any more, so those variants go on
						appearing under a filter this template no longer declares -- the
						same shape as a renamed attribute, one level down.
					</p>
					<ul className="list-inside list-disc">
						{strandedByAxis.map((entry) => (
							<li key={entry.name}>
								<span className="font-medium">{entry.name}</span> --{" "}
								{entry.variants === 1
									? "1 variant uses it"
									: `${entry.variants} variants use it`}
							</li>
						))}
					</ul>
				</div>
			) : null}

			{impact.orphaned.length > 0 ? (
				<div className="grid gap-2 rounded-2xl border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900">
					<p className="font-medium">
						Values that will stop being shown or checked
					</p>
					<p>
						These attribute names are not in the template you are saving. The
						values stay in the database and nothing reads them again -- this is
						what renaming an attribute does, because a rename is a delete and an
						add.
					</p>
					<ul className="list-inside list-disc">
						{impact.orphaned.map((entry) => (
							<li key={entry.name}>
								<span className="font-medium">{entry.name}</span> --{" "}
								{entry.products === 1
									? "1 product has a value"
									: `${entry.products} products have a value`}
							</li>
						))}
					</ul>
				</div>
			) : null}

			{impact.newlyRequired.length > 0 ? (
				<div className="grid gap-2 rounded-2xl border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900">
					<p className="font-medium">Products that could no longer publish</p>
					<p>
						Making an attribute required applies to products that already exist.
						Live products stay live, and are refused only when someone
						republishes them or edits their specifications, images, category,
						HSN code or unit -- other edits still go through.
					</p>
					<ul className="list-inside list-disc">
						{impact.newlyRequired.map((entry) => (
							<li key={entry.name}>
								<span className="font-medium">{entry.name}</span> --{" "}
								{entry.missing} without a value
								{entry.active > 0 ? `, ${entry.active} of them live` : ""}
							</li>
						))}
					</ul>
				</div>
			) : null}

			{impact.narrowedEnums.length > 0 ? (
				<div className="grid gap-2 rounded-2xl border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900">
					<p className="font-medium">
						Values products hold that would no longer be allowed
					</p>
					<p>
						A save that includes a product's specifications is checked against
						the template's allowed values, so these products are refused on any
						such edit until someone changes them. Edits that leave
						specifications alone still go through.
					</p>
					<ul className="list-inside list-disc">
						{impact.narrowedEnums.map((entry) => (
							<li key={entry.name}>
								<span className="font-medium">{entry.name}</span>:{" "}
								{entry.removed.join(", ")} -- {entry.products} affected
							</li>
						))}
					</ul>
				</div>
			) : null}

			{impact.retyped.length > 0 ? (
				<div className="grid gap-2 rounded-2xl border bg-slate-50 p-3 text-sm">
					<p className="font-medium">Attributes changing type</p>
					<p>
						Saving a product validates its specifications against the template,
						so a stored value that does not fit the new type has to be corrected
						before that product can be saved again.
					</p>
					<ul className="list-inside list-disc">
						{impact.retyped.map((entry) => (
							<li key={entry.name}>
								<span className="font-medium">{entry.name}</span>: {entry.from}{" "}
								to {entry.to} -- {entry.stored} with a value
							</li>
						))}
					</ul>
				</div>
			) : null}

			{error ? (
				<p role="alert" className="text-sm text-red-700">
					{error}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onCancel} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={onConfirm} disabled={saving}>
					{saving ? "Saving..." : "Save template"}
				</Button>
			</div>
		</Dialog>
	);
}
